import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api.js';

/**
 * How much of this dealership's stock a buyer on the public website can
 * actually filter for.
 *
 * ONE QUERY KEY, THREE SURFACES. The Inventory button, the dashboard tile and
 * the banner all read this. They must never quote different numbers at each
 * other, and before this hook existed the Inventory button quietly did: it
 * counted the loaded page (50 rows) while the review screen it opened queried
 * the whole tenant. React-query's cache is what keeps the three in step, and it
 * also means the three of them cost one request between them.
 *
 * `enabled` is the permission gate. The endpoint requires `inventory.edit` and
 * returns 403 without it, so calling it for a viewer would be a guaranteed
 * failed request on every page load — and a nag aimed at someone with no power
 * to act on it is noise they learn to scroll past.
 */
export function useListingHealth({ enabled = true } = {}) {
    return useQuery({
        queryKey: ['listing-health'],
        enabled,
        //The server caches this for five minutes and busts on apply; matching
        //that here stops a tab switch from re-requesting something that cannot
        //have changed.
        staleTime: 5 * 60 * 1000,
        //A failure here must not retry in a loop behind a banner nobody asked
        //for. One attempt, then the surfaces render nothing.
        retry: false,
        queryFn: async () => {
            const res = await apiFetch('/enrichment/summary', { method: 'GET' });
            const body = await res.json().catch(() => null);
            if (!res.ok || body?.ok !== true) {
                throw new Error(body?.message || 'Failed to load listing health');
            }
            return body;
        },
    });
}

export default useListingHealth;
